// src/pages/EstimateDetail.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../api";
import { fetchAccess } from "../access";
import { buildDocumentTimestamp } from "../utils/documentTimestamp";
import { ui, uiHandlers } from "../uiStyles";

function money(x) {
  if (x == null) return "$0.00";
  const n = typeof x === "string" ? Number(x) : x;
  return Number(n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d || "—");
  }
}

function dateToInputValue(d) {
  if (!d) return "";
  const s = String(d);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export default function EstimateDetail() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const [inv, setInv] = useState(null);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const [newDesc, setNewDesc] = useState("");
  const [newQty, setNewQty] = useState("1.00");
  const [newUnit, setNewUnit] = useState("0.00");
  const [newTax, setNewTax] = useState("0.0000");
  const [itemError, setItemError] = useState("");
  const [savingItem, setSavingItem] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [selectedLibraryItemId, setSelectedLibraryItemId] = useState("");
  const [saveManualLineItem, setSaveManualLineItem] = useState(false);

  const [dueDate, setDueDate] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [extraExclusions, setExtraExclusions] = useState("");
  const [extraConditions, setExtraConditions] = useState("");
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [documentPaymentNote, setDocumentPaymentNote] = useState("");
  const [documentTerms, setDocumentTerms] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [metaMsg, setMetaMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [emailAction, setEmailAction] = useState(null);
  const [access, setAccess] = useState({ can_send_email: true, can_move_money: true });

  async function loadLibraryItems() {
    try {
      const data = await apiRequest("/products-services/");
      setLibraryItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load Products & Services library", e);
      setLibraryItems([]);
    }
  }

  function applyLibraryItem(itemId) {
    const selected = libraryItems.find(
      (item) => String(item.id) === String(itemId)
    );

    setSelectedLibraryItemId(itemId);

    if (!selected) return;

    setNewDesc(selected.description || selected.name || "");
    setNewQty(String(selected.default_quantity || "1.00"));
    setNewUnit(String(selected.default_rate || "0.00"));
    setNewTax(selected.taxable ? "7.75" : "0");
  }

  async function load() {
    setLoading(true);
    setErr("");
    setMetaMsg("");
    setSuccessMsg("");

    try {
      const data = await apiRequest(`/estimates/${invoiceId}`);
      setInv(data);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setDueDate(dateToInputValue(data?.due_date));
      setCustomerNotes(data?.customer_notes || "");
      setExtraExclusions(
        Array.isArray(data?.extra_exclusions) ? data.extra_exclusions.join("\n") : ""
      );
      setExtraConditions(
        Array.isArray(data?.extra_conditions) ? data.extra_conditions.join("\n") : ""
      );
      setPaymentSchedule(
        Array.isArray(data?.payment_schedule) ? data.payment_schedule : []
      );
      setDocumentPaymentNote(data?.document_payment_note || "");
      setDocumentTerms(data?.document_terms || "");
    } catch (e) {
      setErr(String(e?.message || e || "Failed to load."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAccess().then(setAccess);
  }, []);

  useEffect(() => {
    load();
    loadLibraryItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  async function addItem() {
    if (!newDesc.trim()) return;

    const taxPercent = Number(newTax);
    if (!Number.isFinite(taxPercent) || taxPercent < 0 || taxPercent > 100) {
      setItemError(
        "Tax must be a percentage from 0 to 100 (example: 9.75 = 9.75%)."
      );
      return;
    }

    setSavingItem(true);
    setItemError("");
    setErr("");
    setSuccessMsg("");

    try {
      const payload = {
        description: newDesc.trim(),
        qty: Number(newQty || "0"),
        unit_price: Number(newUnit || "0"),
        tax_rate: taxPercent / 100,
      };

      await apiRequest(`/estimates/${invoiceId}/items`, {
        method: "POST",
        body: payload,
      });

      if (!selectedLibraryItemId && saveManualLineItem) {
        await apiRequest("/products-services/", {
          method: "POST",
          body: {
            item_type: "service",
            name: newDesc.trim(),
            description: newDesc.trim(),
            unit: null,
            default_quantity: Number(newQty || "1"),
            default_rate: Number(newUnit || "0"),
            cost: 0,
            taxable: Number(newTax || "0") > 0,
            sku: null,
            active: true,
          },
        });

        await loadLibraryItems();
      }

      setNewDesc("");
      setNewQty("1.00");
      setNewUnit("0.00");
      setNewTax("0.0000");
      setSelectedLibraryItemId("");
      setSaveManualLineItem(false);
      await load();
    } catch (e) {
      setItemError(String(e?.message || e || "Add item failed."));
    } finally {
      setSavingItem(false);
    }
  }

  async function deleteItem(id) {
    if (!window.confirm("Delete this line?")) return;

    setErr("");
    setSuccessMsg("");

    try {
      await apiRequest(`/estimates/${invoiceId}/items/${id}`, {
        method: "DELETE",
      });
      await load();
    } catch (e) {
      setErr(String(e?.message || e || "Delete failed."));
    }
  }

  function linesFromText(value) {
    return value
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  function updatePaymentMilestone(index, field, value) {
    setPaymentSchedule((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  }

  function addPaymentMilestone() {
    setPaymentSchedule((current) => [
      ...current,
      { label: "", percent: 0, due_when: "" },
    ]);
  }

  function removePaymentMilestone(index) {
    setPaymentSchedule((current) =>
      current.filter((_, itemIndex) => itemIndex !== index)
    );
  }

  async function saveEstimateMeta() {
    setSavingMeta(true);
    setErr("");
    setMetaMsg("");
    setSuccessMsg("");

    try {
      const payload = {
        due_date: dueDate ? dueDate : null,
        customer_notes:
          customerNotes && customerNotes.trim()
            ? customerNotes.trim()
            : null,
        extra_exclusions: linesFromText(extraExclusions),
        extra_conditions: linesFromText(extraConditions),
        payment_schedule: paymentSchedule
          .map((item) => ({
            label: String(item?.label || "").trim(),
            percent: Number(item?.percent || 0),
            due_when: String(item?.due_when || "").trim(),
          }))
          .filter(
            (item) =>
              item.label || item.due_when || Number(item.percent) !== 0
          ),
        document_payment_note: documentPaymentNote.trim(),
        document_terms: documentTerms.trim(),
      };

      await apiRequest(`/estimates/${invoiceId}`, {
        method: "PATCH",
        body: payload,
      });

      setMetaMsg("Saved.");
      await load();
    } catch (e) {
      setErr(String(e?.message || e || "Failed to save."));
    } finally {
      setSavingMeta(false);
      setTimeout(() => setMetaMsg(""), 2500);
    }
  }

  async function handleConvertToInvoice() {
    if (!window.confirm("Convert this estimate into an invoice?")) return;

    setErr("");
    setSuccessMsg("");

    try {
      const data = await apiRequest(`/estimates/${invoiceId}/convert-to-invoice`, {
        method: "POST",
      });

      navigate(`/invoices/${data.id}`);
    } catch (e) {
      setErr(String(e?.message || e || "Convert failed."));
    }
  }


  async function saveCommunicationNote() {
    if (!emailAction?.jobId) {
      setErr("This estimate is not linked to a project/job yet.");
      return;
    }

    try {
      const now = new Date().toLocaleString();
      const me = await apiRequest("/auth/me");
      const senderEmail = me?.email || "unknown user";

      const note = `[CLIENT COMMUNICATION]

Document Type: ${emailAction.documentType}
Document Number: ${emailAction.documentNumber}

PDF File:
${emailAction.fileName}

Recipient:
${emailAction.clientEmail}

Date/Time:
${now}

Sent By:
${senderEmail}

Subject:
${emailAction.subject}

Delivery Method:
Tenant Email Client

Status:
Marked sent by user`;

      await apiRequest(
        `/notes/job/${emailAction.jobId}`,
        {
          method: "POST",
          body: {
            content: note,
          },
        }
      );

      // Structured reporting ledger
      await apiRequest(
        "/document-activity",
        {
          method: "POST",
          body: {
            job_id: emailAction.jobId,
            client_id: inv?.client_id || null,

            document_type: "ESTIMATE",
            document_id: Number(invoiceId),
            document_number: emailAction.documentNumber,

            activity_type: "EMAILED",

            amount: inv?.total || null,

            email_to: emailAction.clientEmail,
            subject: emailAction.subject,
            file_name: emailAction.fileName,

            notes: "Estimate emailed through tenant email client",
          },
        }
      );

      setSuccessMsg("Communication note + ledger activity saved.");
      setEmailAction(null);

    } catch (e) {
      console.error(e);

      setErr(
        e?.message || "Failed to save communication note."
      );
    }
  }

  async function handleSendEstimate() {
    const gate = await fetchAccess();
    if (!gate.can_send_email) return;

    setErr("");
    setSuccessMsg("");
    setEmailAction(null);

    if (!inv) {
      setErr("Estimate is not loaded yet.");
      return;
    }

    const clientEmail =
      inv.client_email ||
      inv.email ||
      inv.client?.email ||
      "";

    const clientName =
      inv.client_company_name ||
      inv.client_contact_name ||
      inv.client_name ||
      inv.client?.company_name ||
      inv.client?.contact_name ||
      inv.client?.name ||
      "Client";

    if (!clientEmail) {
      setErr("Client email is missing. Add a client email before preparing this estimate email.");
      return;
    }

    const estimateNumber = inv.estimate_number || invoiceId;

    const timestamp = buildDocumentTimestamp();

    const jobPart = inv?.job_id ? `-JOB${inv.job_id}` : "";

    const pdfFileName =
      `Estimate-${estimateNumber}${jobPart}-${timestamp}.pdf`;

    try {
      const printUrl = `/estimates/${invoiceId}/print`;
      window.open(printUrl, "_blank", "noopener,noreferrer");

      const subject = `Estimate ${estimateNumber}`;

      const body = `Hi ${clientName},

I hope you’re doing well.

I prepared the estimate for your review.

Please see the attached estimate PDF and let me know if you have any questions or if you would like to move forward with scheduling.

Thank you,

`;

      const mailto =
        `mailto:${encodeURIComponent(clientEmail)}` +
        `?subject=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`;

      window.location.href = mailto;

      // ========================================================
      // OET V1 — Operational Event Telemetry
      // --------------------------------------------------------
      // Tracks estimate email preparation activity.
      // This becomes part of tenant sales/quote telemetry.
      // ========================================================
      try {
        await fetch("/api/document-activity", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "x-tenant": localStorage.getItem("x-tenant") || "dfasio",
          },
          body: JSON.stringify({
            job_id: inv?.job_id || null,
            client_id: inv?.client_id || null,
            document_type: "estimate",
            document_id: inv?.id || null,
            document_number: estimateNumber,
            activity_type: "EMAIL_PREPARED",
            email_to: clientEmail,
            subject,
            file_name: pdfFileName,
            notes: "Estimate Prepare Email triggered from Estimate Detail page.",
          }),
        });
      } catch (telemetryError) {
        console.error("OET telemetry error:", telemetryError);
      }

      setEmailAction({
        fileName: pdfFileName,
        clientEmail,
        subject,
        jobId: inv?.job_id,
        documentType: "Estimate",
        documentNumber: estimateNumber,
      });

      setSuccessMsg(
        "Print/Save PDF page opened. Save the estimate as a PDF, then attach it to the email draft before sending."
      );
      setTimeout(() => setSuccessMsg(""), 7000);
    } catch (e) {
      setErr(String(e?.message || e || "Failed to prepare estimate email."));
    }
  }

  if (loading) {
    return <div style={{ padding: 24, color: "#ffffff" }}>Loading…</div>;
  }

  if (err) {
    return (
      <div style={{ padding: 24, color: "#ffffff" }}>
        <div
          style={{
            background: "rgba(255,138,138,0.10)",
            border: "1px solid rgba(255,138,138,0.30)",
            color: "#ff8a8a",
            padding: "10px 12px",
            borderRadius: 10,
            marginBottom: 14,
          }}
        >
          {err}
        </div>
        <p>
          <button onClick={load}>Retry</button>{" "}
          <button onClick={() => navigate(-1)}>Back</button>
        </p>
      </div>
    );
  }

  if (!inv) return null;

  const totals = {
    subtotal: money(inv.subtotal),
    tax: money(inv.tax_total),
    total: money(inv.total),
  };

  const page = ui.page;
  const card = ui.card;
  const subCard = ui.subCard;
  const input = ui.input;
  const linkStyle = ui.link;
  const tableTh = ui.tableTh;
  const tableTd = ui.tableTd;

  const focusHandlers = uiHandlers.focus;
  const btnPrimary = ui.btnPrimary;
  const btnSecondary = ui.btnSecondary;
  const btnDanger = ui.btnDanger;

  const primaryHoverHandlers = uiHandlers.primaryHover;
  const lineBtn = {
    width: 128,
    height: 36,
    minWidth: 128,
    maxWidth: 128,
    minHeight: 36,
    maxHeight: 36,
    padding: 0,
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    boxShadow: "none",
  };
  const removeBtn = {
    ...lineBtn,
    background: "transparent",
    color: "#ef4444",
    border: "1px solid #ef4444",
  };
  const addBtn = {
    ...lineBtn,
    background: "#f5c542",
    color: "#0b1b2b",
    border: "1px solid #f5c542",
  };
  const secondaryHoverHandlers = uiHandlers.secondaryHover;

  return (
    <div style={page}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={btnSecondary}
          {...secondaryHoverHandlers}
        >
          ← Back
        </button>
        <Link to="/estimates" style={linkStyle}>
          Estimates
        </Link>
      </div>

      <div style={card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h3 style={{ margin: "0 0 4px", color: "#ffffff", fontSize: 18 }}>
              Estimate {inv.estimate_number || `EST-${invoiceId}`}
            </h3>

            <div
              style={{
                color: "#9fb0c3",
                fontSize: 14,
                display: "grid",
                gridTemplateColumns: "90px 1fr",
                rowGap: 8,
                columnGap: 10,
                alignItems: "center",
                marginTop: 10,
              }}
            >
              <span>Issue Date:</span>
              <span style={{ color: "#ffffff" }}>{fmtDate(inv.issue_date)}</span>

              <span>Due Date:</span>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <input
                  type="date"
                  value={dueDate || ""}
                  onChange={(e) => setDueDate(e.target.value)}
                  style={{ ...input, width: 160 }}
                  {...focusHandlers}
                />

                <button
                  className="no-print"
                  onClick={saveEstimateMeta}
                  disabled={savingMeta}
                  style={btnPrimary}
                  title="Saves Due Date + Customer Notes"
                  {...primaryHoverHandlers}
                >
                  {savingMeta ? "Saving..." : "Save"}
                </button>

                {metaMsg && (
                  <span style={{ color: "#86efac", fontSize: 13 }}>
                    {metaMsg}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ minWidth: 280 }}>
            {emailAction && (
              <div
                className="no-print"
                style={{
                  background: "rgba(224,184,79,0.12)",
                  border: "1px solid rgba(224,184,79,0.45)",
                  color: "#f7e7b2",
                  padding: "12px 14px",
                  borderRadius: 10,
                  marginBottom: 10,
                  lineHeight: 1.5,
                }}
              >
                <div style={{ fontWeight: 800, marginBottom: 6 }}>
                  Take Action: Attach the Estimate PDF
                </div>

                <div style={{ fontSize: 14 }}>
                  Your email app should now be open. Attach this downloaded file:
                </div>

                <div
                  style={{
                    marginTop: 8,
                    padding: "8px 10px",
                    background: "rgba(0,0,0,0.18)",
                    borderRadius: 8,
                    fontFamily: "monospace",
                    fontSize: 13,
                    color: "#ffffff",
                  }}
                >
                  {emailAction.fileName}
                </div>

                <div style={{ marginTop: 8, fontSize: 13, color: "#d7e7f5" }}>
                  Most browsers save it in your Downloads folder unless you chose another location.
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={saveCommunicationNote}
                    style={{
                      padding: "7px 10px",
                      background: "#1d4ed8",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  >
                    Mark Sent & Save Job Note
                  </button>
                </div>
              </div>
            )}

            {successMsg && (
              <div
                style={{
                  background: "rgba(134,239,172,0.10)",
                  border: "1px solid rgba(134,239,172,0.30)",
                  color: "#86efac",
                  padding: "10px 12px",
                  borderRadius: 10,
                  marginBottom: 10,
                }}
              >
                {successMsg}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link
                className="no-print"
                to={`/estimates/${invoiceId}/print`}
                style={btnSecondary}
              >
                Print
              </Link>

              {access.can_send_email && (
              <button
                type="button"
                className="no-print"
                style={btnSecondary}
                onClick={handleSendEstimate}
                {...secondaryHoverHandlers}
              >
                Prepare Email
              </button>
              )}

              <button
                type="button"
                className="no-print"
                style={btnPrimary}
                onClick={handleConvertToInvoice}
                {...primaryHoverHandlers}
              >
                Convert to Invoice
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={card}>
        {itemError && (
          <div
            role="alert"
            style={{
              background: "rgba(255,138,138,0.10)",
              border: "1px solid rgba(255,138,138,0.30)",
              color: "#ff8a8a",
              padding: "10px 12px",
              borderRadius: 10,
              marginBottom: 14,
            }}
          >
            {itemError}
          </div>
        )}


        <style>{`
          .jt-doc-lines { display: flex; flex-direction: column; gap: 8px; }
          .jt-doc-line {
            display: grid;
            grid-template-columns: minmax(0, 1fr) 100px 130px 110px 120px 140px;
            gap: 10px;
            align-items: center;
          }
          .jt-doc-line > * { min-width: 0; }
          .jt-doc-head {
            color: #ffffff;
            font-weight: 700;
            font-size: 14px;
            background: #0f2235;
            border-radius: 10px;
            padding: 10px 0;
          }
          .jt-doc-line > div { padding-left: 12px; }
          .jt-doc-line .num { text-align: left; }
          .jt-doc-line input {
            width: 100% !important;
            max-width: none !important;
            min-width: 0 !important;
            box-sizing: border-box;
            text-align: left !important;
          }
        `}</style>

        <div className="jt-doc-lines">
          <div className="jt-doc-line jt-doc-head">
            <div>Description</div>
            <div className="num">Qty</div>
            <div className="num">Unit Price</div>
            <div className="num">Tax</div>
            <div className="num">Line Total</div>
            <div>Actions</div>
          </div>
          {items.map((it) => (
            <div className="jt-doc-line" key={it.id}>
              <div style={{ overflowWrap: "anywhere" }}>{it.description}</div>
              <div className="num">{Number(it.qty || 0).toFixed(2)}</div>
              <div className="num">{money(it.unit_price)}</div>
              <div className="num">{(Number(it.tax_rate || 0) * 100).toFixed(2)}%</div>
              <div className="num">{money(it.line_total)}</div>
              <button type="button" onClick={() => deleteItem(it.id)} style={removeBtn}>Remove</button>
            </div>
          ))}
          <div className="jt-doc-line">
            <input
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Description"
              style={input}
              {...focusHandlers}
            />
            <input
              value={newQty}
              onChange={(e) => setNewQty(e.target.value)}
              placeholder="Qty"
              aria-label="Quantity"
              style={input}
              {...focusHandlers}
            />
            <input
              value={newUnit}
              onChange={(e) => setNewUnit(e.target.value)}
              placeholder="Price"
              aria-label="Unit price"
              style={input}
              {...focusHandlers}
            />
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={newTax}
              onChange={(e) => setNewTax(e.target.value)}
              placeholder="Tax %"
              aria-label="Tax percentage, for example 9.75 equals 9.75 percent"
              style={input}
              {...focusHandlers}
            />
            <div className="num" style={{ color: "#9fb0c3" }}>
              {money((Number(newQty) || 0) * (Number(newUnit) || 0))}
            </div>
            <button
              type="button"
              onClick={addItem}
              disabled={savingItem || !newDesc.trim()}
              style={{ ...addBtn, opacity: savingItem || !newDesc.trim() ? 0.7 : 1 }}
            >
              Add Item
            </button>
          </div>
        </div>
        <div className="no-print" style={{ marginTop: 12, maxWidth: 380 }}>
          <select value={selectedLibraryItemId} onChange={(e) => applyLibraryItem(e.target.value)} style={input}>
            <option value="">Select Line Item</option>
            {libraryItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} — ${item.default_rate}
              </option>
            ))}
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#9fb0c3", marginTop: 8 }}>
            <input
              type="checkbox"
              checked={saveManualLineItem}
              onChange={(e) => setSaveManualLineItem(e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            Save Line Item
          </label>
        </div>

      </div>

      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 10, color: "#ffffff" }}>
          Customer Notes / Payment Terms
        </div>
        <textarea
          value={customerNotes}
          onChange={(e) => setCustomerNotes(e.target.value)}
          placeholder="Example: Estimate valid for 15 days. Deposit required before scheduling. Materials subject to availability."
          style={{
            ...input,
            minHeight: 110,
            resize: "vertical",
          }}
          {...focusHandlers}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 10,
            flexWrap: "wrap",
          }}
        >
          <button
            className="no-print"
            onClick={saveEstimateMeta}
            disabled={savingMeta}
            style={btnPrimary}
            {...primaryHoverHandlers}
          >
            {savingMeta ? "Saving…" : "Save Notes"}
          </button>
          <div style={{ fontSize: 12, color: "#9fb0c3" }}>
            These notes should appear on Print/PDF + emailed estimate.
          </div>
        </div>
      </div>

      <div
        style={{
          ...card,
          border: "1px solid rgba(224,184,79,0.38)",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 18, color: "#ffffff" }}>
          Document Station
        </div>
        <div style={{ marginTop: 4, marginBottom: 16, color: "#9fb0c3", fontSize: 13 }}>
          These estimate-specific terms are preserved even if tenant defaults change later.
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          <label>
            <div style={{ marginBottom: 6, color: "#dbe7f3", fontWeight: 700 }}>
              Exclusions — one per line
            </div>
            <textarea
              value={extraExclusions}
              onChange={(e) => setExtraExclusions(e.target.value)}
              placeholder="Work or materials not included in this estimate"
              style={{ ...input, width: "100%", minHeight: 82, boxSizing: "border-box" }}
              {...focusHandlers}
            />
          </label>

          <label>
            <div style={{ marginBottom: 6, color: "#dbe7f3", fontWeight: 700 }}>
              Conditions — one per line
            </div>
            <textarea
              value={extraConditions}
              onChange={(e) => setExtraConditions(e.target.value)}
              placeholder="Conditions that apply to this estimate"
              style={{ ...input, width: "100%", minHeight: 82, boxSizing: "border-box" }}
              {...focusHandlers}
            />
          </label>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 8 }}>
              <div style={{ color: "#dbe7f3", fontWeight: 700 }}>
                Payment Schedule
              </div>
              <button
                type="button"
                className="no-print"
                onClick={addPaymentMilestone}
                style={{ ...btnSecondary, padding: "7px 10px" }}
              >
                + Add Payment
              </button>
            </div>

            {paymentSchedule.length === 0 ? (
              <div style={{ color: "#9fb0c3", fontSize: 13 }}>
                No payment milestones. Use “Add Payment” if this estimate needs them.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {paymentSchedule.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(140px, 1fr) 100px minmax(190px, 1.5fr) auto",
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                    <input
                      value={item?.label || ""}
                      onChange={(e) => updatePaymentMilestone(index, "label", e.target.value)}
                      placeholder="Deposit"
                      aria-label={`Payment ${index + 1} label`}
                      style={input}
                      {...focusHandlers}
                    />
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={item?.percent ?? 0}
                      onChange={(e) => updatePaymentMilestone(index, "percent", e.target.value)}
                      aria-label={`Payment ${index + 1} percent`}
                      style={input}
                      {...focusHandlers}
                    />
                    <input
                      value={item?.due_when || ""}
                      onChange={(e) => updatePaymentMilestone(index, "due_when", e.target.value)}
                      placeholder="When this payment is due"
                      aria-label={`Payment ${index + 1} due condition`}
                      style={input}
                      {...focusHandlers}
                    />
                    <button
                      type="button"
                      className="no-print"
                      onClick={() => removePaymentMilestone(index)}
                      style={{ ...btnDanger, padding: "7px 10px" }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label>
            <div style={{ marginBottom: 6, color: "#dbe7f3", fontWeight: 700 }}>
              Payment Instructions
            </div>
            <textarea
              value={documentPaymentNote}
              onChange={(e) => setDocumentPaymentNote(e.target.value)}
              placeholder="Example: Pay by Zelle to admin@dfasio.com"
              style={{ ...input, width: "100%", minHeight: 72, boxSizing: "border-box" }}
              {...focusHandlers}
            />
          </label>

          <label>
            <div style={{ marginBottom: 6, color: "#dbe7f3", fontWeight: 700 }}>
              Terms
            </div>
            <textarea
              value={documentTerms}
              onChange={(e) => setDocumentTerms(e.target.value)}
              placeholder="Terms that apply to this estimate"
              style={{ ...input, width: "100%", minHeight: 110, boxSizing: "border-box" }}
              {...focusHandlers}
            />
          </label>

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              className="no-print"
              onClick={saveEstimateMeta}
              disabled={savingMeta}
              style={{ ...btnPrimary, opacity: savingMeta ? 0.7 : 1 }}
              {...primaryHoverHandlers}
            >
              {savingMeta ? "Saving…" : "Save Document"}
            </button>
            {metaMsg && <span style={{ color: "#86efac", fontSize: 13 }}>{metaMsg}</span>}
          </div>
        </div>
      </div>

      <div
        style={{
          ...subCard,
          display: "grid",
          gridTemplateColumns: "1fr 220px",
        }}
      >
        <div />
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 6 }}>
            <div style={{ color: "#dbe7f3" }}>Subtotal</div>
            <div style={{ color: "#ffffff" }}>{totals.subtotal}</div>
            <div style={{ color: "#dbe7f3" }}>Tax</div>
            <div style={{ color: "#ffffff" }}>{totals.tax}</div>
            <div style={{ fontWeight: 700, color: "#ffffff" }}>Total</div>
            <div style={{ fontWeight: 700, color: "#ffffff" }}>{totals.total}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
